import Icon from "@ui_components/Icon";
import React, { useEffect, useLayoutEffect, useRef } from "react";

import styles from "./styles/rootLoader.module.scss";

const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export interface RootLoaderProps {
    icon?: string;
    title?: React.ReactNode;
    description?: React.ReactNode;
}

const RootLoader = ({ icon, title, description }: RootLoaderProps) => {
    const spinnerRef = useRef<HTMLDivElement>(null);
    const pulseRef = useRef<HTMLSpanElement>(null);

    useIsomorphicLayoutEffect(() => {
        const syncNode = (node: HTMLElement | null) => {
            if (!node) return;
            // Resume animation at the shared clock's phase so sequential loaders look continuous.
            const durationMs = parseFloat(getComputedStyle(node).animationDuration) * 1000 || 1000;
            if (durationMs > 0) {
                node.style.animationDelay = `-${performance.now() % durationMs}ms`;
            }
        };

        syncNode(spinnerRef.current);
        syncNode(pulseRef.current);
    }, [icon]);

    return (
        <div className={styles.lumenoreBaseRootLoaderContainer}>
            <div className={styles.lumenoreRootLoaderContent}>
                <div className={styles.iconGroup}>
                    {icon ? (
                        <div className={styles.logoContainer}>
                            <div ref={spinnerRef} className={styles.outerRing} />
                            <span ref={pulseRef} className={styles.innerPulse}>
                                <Icon path={icon} className={styles.pulseIcon} size={28} />
                            </span>
                        </div>
                    ) : (
                        <div ref={spinnerRef} className={styles.lumenoreRootLoader} />
                    )}
                </div>
                {(title || description) && (
                    <div className={styles.textGroup}>
                        {title && <span className={styles.lumenoreRootLoaderTitle}>{title}</span>}
                        {description && <span className={styles.lumenoreRootLoaderDescription}>{description}</span>}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RootLoader;
